import {
  MenuItem,
  MenuSeparator,
  toast,
  Tooltip,
  useConfirmModal,
} from '@affine/component';
import { Guard, useGuard } from '@affine/core/components/guard';
import { useBlockSuiteMetaHelper } from '@affine/core/components/hooks/affine/use-block-suite-meta-helper';
import { useEnableCloud } from '@affine/core/components/hooks/affine/use-enable-cloud';
import { useTrashDoc } from '@affine/core/components/hooks/affine/use-trash-doc';
import { useSplitViewViewportAvailable } from '@affine/core/components/hooks/use-split-view-viewport-available';
import { IsFavoriteIcon } from '@affine/core/components/pure/icons';
import { type Doc, DocsService } from '@affine/core/modules/doc';
import type { Editor } from '@affine/core/modules/editor';
import { EditorsService } from '@affine/core/modules/editor';
import { CompatibleFavoriteItemsAdapter } from '@affine/core/modules/favorite';
import {
  beginObjectConfigurationTrace,
  recordObjectConfigurationTraceEvent,
} from '@affine/core/modules/permissions/utils/object-configuration-trace';
import { DocConfigurationDialog } from '@affine/core/modules/share-menu';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import {
  DeleteIcon,
  DuplicateIcon,
  InformationIcon,
  OpenInNewIcon,
  SettingsIcon,
  SplitViewIcon,
} from '@blocksuite/icons/rc';
import { FrameworkScope, useLiveData, useServices } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { defineNavigationPanelNodeOperations } from '../../tree/operation-registry';
import type { NodeOperation } from '../../tree/types';

const DOC_NODE_OPERATION_IDS = {
  info: 'doc:info',
  configure: 'doc:configure',
  duplicate: 'doc:duplicate',
  openNewTab: 'doc:open-new-tab',
  openSplitView: 'doc:open-split-view',
  favorite: 'doc:favorite',
  trashSeparator: 'doc:trash-separator',
  trash: 'doc:trash',
} as const;

type DocNodeOperationId =
  (typeof DOC_NODE_OPERATION_IDS)[keyof typeof DOC_NODE_OPERATION_IDS];

const OpenInSplitViewMenuItem = ({ onClick }: { onClick: () => void }) => {
  const t = useI18n();
  const viewportAvailable = useSplitViewViewportAvailable();

  return (
    <Tooltip
      content={
        viewportAvailable
          ? undefined
          : t['com.streamlain.workbench.split-view.viewport-too-small']()
      }
      side="right"
    >
      <div>
        <MenuItem
          prefixIcon={<SplitViewIcon />}
          onClick={onClick}
          disabled={!viewportAvailable}
        >
          {t['com.streamlain.workbench.split-view.page-menu-open']()}
        </MenuItem>
      </div>
    </Tooltip>
  );
};

const DocConfigureMenuItem = ({ docId }: { docId: string }) => {
  const t = useI18n();
  const canManageUsers = useGuard('Doc_Users_Manage', docId);
  const confirmEnableCloud = useEnableCloud();
  const { docsService, workspaceService } = useServices({
    DocsService,
    WorkspaceService,
  });
  const [open, setOpen] = useState(false);
  const [currentDoc, setCurrentDoc] = useState<Doc | null>(null);
  const [currentEditor, setCurrentEditor] = useState<Editor | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const releaseRef = useRef<(() => void) | null>(null);

  const closeLoadedDoc = useCallback(() => {
    editorRef.current?.dispose();
    editorRef.current = null;
    releaseRef.current?.();
    releaseRef.current = null;
    setCurrentEditor(null);
    setCurrentDoc(null);
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setOpen(false);
        closeLoadedDoc();
        return;
      }

      if (!currentDoc) {
        try {
          recordObjectConfigurationTraceEvent('doc-open-started');
          const { doc, release } = docsService.open(docId);
          recordObjectConfigurationTraceEvent('doc-open-finished');
          recordObjectConfigurationTraceEvent('editor-create-started');
          const editor = doc.scope.get(EditorsService).createEditor();
          recordObjectConfigurationTraceEvent('editor-create-finished');
          releaseRef.current = release;
          editorRef.current = editor;
          setCurrentDoc(doc);
          setCurrentEditor(editor);
        } catch (err) {
          console.error('Failed to open doc configuration:', err);
          toast(t['com.streamlain.share-menu.open-failed']());
          return;
        }
      }

      track.$.sharePanel.$.open();
      setOpen(true);
    },
    [closeLoadedDoc, currentDoc, docId, docsService, t]
  );

  useEffect(
    () => () => {
      editorRef.current?.dispose();
      editorRef.current = null;
      releaseRef.current?.();
      releaseRef.current = null;
    },
    []
  );

  if (canManageUsers !== true) {
    return null;
  }

  return (
    <>
      <MenuItem
        prefixIcon={<SettingsIcon />}
        data-testid="doc-menu-configure"
        onSelect={event => {
          // The dialog state lives in this menu item. Keep the parent menu
          // mounted while the portalled dialog is open.
          event.preventDefault();
          beginObjectConfigurationTrace('doc');
          handleOpenChange(true);
        }}
      >
        {t['com.streamlain.streamlain.common.configure']()}
      </MenuItem>
      {currentDoc && currentEditor ? (
        <FrameworkScope scope={currentDoc.scope}>
          <FrameworkScope scope={currentEditor.scope}>
            <DocConfigurationDialog
              title={t['com.streamlain.streamlain.common.configure']()}
              open={open}
              onOpenChange={handleOpenChange}
              workspaceMetadata={workspaceService.workspace.meta}
              currentPage={currentDoc.blockSuiteDoc}
              hideExport
              onEnableAffineCloud={() =>
                confirmEnableCloud(workspaceService.workspace, {
                  openPageId: docId,
                })
              }
            />
          </FrameworkScope>
        </FrameworkScope>
      ) : null}
    </>
  );
};

export const useNavigationPanelDocNodeOperations = (
  docId: string,
  options: {
    openInfoModal: () => void;
    openNodeCollapsed: () => void;
    hideTrashOperation?: boolean;
  }
): NodeOperation[] => {
  const t = useI18n();
  const { workbenchService, docsService, compatibleFavoriteItemsAdapter } =
    useServices({
      DocsService,
      WorkbenchService,
      CompatibleFavoriteItemsAdapter,
    });
  const { openConfirmModal } = useConfirmModal();
  const trashDoc = useTrashDoc();

  const docRecord = useLiveData(docsService.list.doc$(docId));

  const favorite = useLiveData(
    useMemo(() => {
      return compatibleFavoriteItemsAdapter.isFavorite$(docId, 'doc');
    }, [docId, compatibleFavoriteItemsAdapter])
  );

  const { duplicate } = useBlockSuiteMetaHelper();
  const handleDuplicate = useCallback(() => {
    duplicate(docId, true);
    track.$.navigationPanel.docs.createDoc();
  }, [docId, duplicate]);
  const handleOpenInfoModal = useCallback(() => {
    track.$.docInfoPanel.$.open();
    options.openInfoModal();
  }, [options]);

  const handleMoveToTrash = useCallback(() => {
    if (!docRecord) {
      return;
    }
    openConfirmModal({
      title: t['com.streamlain.moveToTrash.title'](),
      description: t['com.streamlain.moveToTrash.confirmModal.description']({
        title: docRecord.title$.value,
      }),
      confirmText: t['com.streamlain.moveToTrash.confirmModal.confirm'](),
      cancelText: t['com.streamlain.moveToTrash.confirmModal.cancel'](),
      confirmButtonOptions: {
        variant: 'error',
      },
      async onConfirm() {
        await trashDoc(docRecord);
        track.$.navigationPanel.docs.deleteDoc({
          control: 'button',
        });
      },
    });
  }, [docRecord, openConfirmModal, t, trashDoc]);

  const handleOpenInNewTab = useCallback(() => {
    workbenchService.workbench.openDoc(docId, {
      at: 'new-tab',
    });
    track.$.navigationPanel.docs.openDoc();
    track.$.navigationPanel.organize.openInNewTab({
      type: 'doc',
    });
  }, [docId, workbenchService]);

  const handleOpenInSplitView = useCallback(() => {
    workbenchService.workbench.openDoc(docId, {
      at: 'beside',
    });
    track.$.navigationPanel.docs.openDoc();
    track.$.navigationPanel.organize.openInSplitView({
      type: 'doc',
    });
  }, [docId, workbenchService.workbench]);

  const handleToggleFavoriteDoc = useCallback(() => {
    compatibleFavoriteItemsAdapter.toggle(docId, 'doc');
    track.$.navigationPanel.organize.toggleFavorite({
      type: 'doc',
    });
  }, [docId, compatibleFavoriteItemsAdapter]);

  return useMemo(
    () =>
      defineNavigationPanelNodeOperations<DocNodeOperationId>([
        // sl-jmyl.8: "Verknüpfte Seite hinzufügen" inline button removed
        {
          id: DOC_NODE_OPERATION_IDS.info,
          index: 50,
          view: (
            <MenuItem
              prefixIcon={<InformationIcon />}
              onClick={handleOpenInfoModal}
            >
              {t['com.streamlain.page-properties.page-info.view']()}
            </MenuItem>
          ),
        },
        // sl-jmyl.8: "Verknüpfte Seite hinzufügen" menu item removed
        {
          id: DOC_NODE_OPERATION_IDS.configure,
          index: 9998,
          view: <DocConfigureMenuItem docId={docId} />,
        },
        {
          id: DOC_NODE_OPERATION_IDS.duplicate,
          index: 99,
          view: (
            <MenuItem prefixIcon={<DuplicateIcon />} onClick={handleDuplicate}>
              {t['com.streamlain.header.option.duplicate']()}
            </MenuItem>
          ),
        },
        {
          id: DOC_NODE_OPERATION_IDS.openNewTab,
          index: 99,
          view: (
            <MenuItem
              prefixIcon={<OpenInNewIcon />}
              onClick={handleOpenInNewTab}
            >
              {t['com.streamlain.workbench.tab.page-menu-open']()}
            </MenuItem>
          ),
        },
        {
          id: DOC_NODE_OPERATION_IDS.openSplitView,
          index: 100,
          view: <OpenInSplitViewMenuItem onClick={handleOpenInSplitView} />,
        },
        {
          id: DOC_NODE_OPERATION_IDS.favorite,
          index: 199,
          view: (
            <MenuItem
              prefixIcon={<IsFavoriteIcon favorite={favorite} />}
              onClick={handleToggleFavoriteDoc}
            >
              {favorite
                ? t['com.streamlain.favoritePageOperation.remove']()
                : t['com.streamlain.favoritePageOperation.add']()}
            </MenuItem>
          ),
        },
        ...(options.hideTrashOperation === true
          ? []
          : [
              {
                id: DOC_NODE_OPERATION_IDS.trashSeparator,
                index: 9999,
                view: <MenuSeparator key="menu-separator" />,
              },
              {
                id: DOC_NODE_OPERATION_IDS.trash,
                index: 10000,
                view: (
                  <Guard docId={docId} permission="Doc_Trash">
                    {canMoveToTrash => (
                      <MenuItem
                        type={'danger'}
                        prefixIcon={<DeleteIcon />}
                        onClick={handleMoveToTrash}
                        disabled={!canMoveToTrash}
                      >
                        {t['com.streamlain.moveToTrash.title']()}
                      </MenuItem>
                    )}
                  </Guard>
                ),
              },
            ]),
      ]),
    [
      docId,
      favorite,
      handleDuplicate,
      handleMoveToTrash,
      handleOpenInNewTab,
      handleOpenInSplitView,
      handleOpenInfoModal,
      handleToggleFavoriteDoc,
      options.hideTrashOperation,
      t,
    ]
  );
};
